/* ============================================================================
   GABBIE CHARM BUILDER — necklace customizer
   ----------------------------------------------------------------------------
   THREE FILES MAKE UP THIS FEATURE:
     1. the Custom Liquid block  — config assigns, <style>, empty mount nodes,
                                   and a JSON <script> holding the CHAIN data
     2. this asset               — the whole application (no framework, ES5)
     3. sections/cb-charm-data.liquid — a data-only section fetched over the
                                   Section Rendering API for the CHARM data
   Why charms come over the wire: a plain Liquid loop over a collection is
   capped at 50 products; paginate inside a section is not. The section also
   preserves metafields and per-variant inventory quantities.

   HOW IT WORKS
     State lives in one plain object (`state`) plus two module-level arrays
     (CHAINS, CHARMS). Every mutation calls render(), which rebuilds the three
     step sections as an HTML string and assigns it to innerHTML. There is no
     virtual DOM, no diffing, and no framework — the whole UI is cheap enough
     to redraw wholesale, and doing so keeps state and markup impossible to
     desync.

     The live preview is NOT rendered inside this block. It is a "portal" node
     that gets physically moved into the theme's product gallery so the charms
     appear over the real product photo. See the GALLERY MOUNT section.

   INVARIANTS WORTH KNOWING
     - Chains are separate products, one per chain — never variants.
     - Pricing is flat: chain price + sum of charm prices. No tiers.
     - A charm may repeat; each repeat is its own instance with its own uid.
     - Inventory caps are per variant, and enforced both in the UI and again
       immediately before the cart POST.
     - builder_charm_image is trusted to be an already-transparent PNG. This
       code never processes images.
   ============================================================================ */

(function () {
  // Bail if the block is missing, and guard against double-initialisation —
  // theme section reloads in the editor can run this file more than once.
  var root = document.getElementById("cb-charm-builder");
  if (!root || root.dataset.cbReady === "1") return;
  root.dataset.cbReady = "1";

  var DATA = {};
  try { DATA = JSON.parse(root.querySelector("[data-charm-builder-data]").textContent); }
  catch (e) { console.warn("Charm builder: could not parse block data.", e); return; }


/* ==========================================================================
   CONFIG
   Read from the block; MOUNT_SELECTORS is the only theme-coupled part.
   ========================================================================== */

  // MAX_CHARMS is a SAFETY CEILING, not the source of truth. Real capacity comes
  // off the selected base product's anchor (see capacityOf) and this only applies
  // when that product declares none.
  var MAX_CHARMS = DATA.maxCharms || 10;
  var MONEY_FORMAT = DATA.moneyFormat || "{{amount}}";
  var DEFAULT_ANCHOR = { mode: "curve", start: [14, 40], end: [86, 40], sag: 26, charmScale: 0.12, charmRotate: 0.85, capacity: null };
  var CATEGORY_ORDER = ["Filipino","Travel","Tropical","Nature","Celestial","Food","Hobbies","Holidays","Letters","Others"];
  var MOUNT_SELECTORS = [
    ".slider__item.is-active .product-media--image .img-ar",
    ".slider__item.is-active .product-media-wrapper",
    ".product-media--image .img-ar"
  ];
  var FALLBACK_ASPECT = 0.8;

  if (!(DATA.chains || []).length) console.warn('Charm builder: collection "' + DATA.chainHandle + '" returned no chain products.');
  var CHARM_SECTION = "cb-charm-data";

  // Amounts sum in cents and render through the shop's own Liquid money_format
  // string — no currency pattern is hardcoded here.
  function money(cents) {
    var v = (cents / 100).toFixed(2).split(".");
    var c = v[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    var d = v[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return MONEY_FORMAT
      .replace(/\{\{\s*amount\s*\}\}/g, c + "." + v[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, c)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, d + "," + v[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, d)
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, v[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'") + "." + v[1]);
  }


/* ==========================================================================
   CHAIN DATA
   Inline from the block. Each chain carries its own preview anchor geometry.
   ========================================================================== */

  // builder_anchor is authored per BASE PRODUCT as JSON, in PERCENT of that
  // product's photo, and carries both the preview geometry and the charm
  // capacity. "mode" decides how charms are placed:
  //
  //   curve   (default, and what an untagged necklace anchor means)
  //           {start:[x,y], end:[x,y], sag, charmScale, charmRotate, max}
  //           charms distribute along a quadratic Bézier and redistribute on
  //           every add, remove and reorder.
  //   cluster {center:[x,y], gap, charmScale, max}
  //           charms anchor to a centre point and spread symmetrically outward
  //           with a fixed gap, so adding a third shifts the existing two.
  //   fan     {pivot:[x,y], spread, radius, charmScale, max}
  //           charms hang from one point on the chain and splay outward on an
  //           arc, each rotated to sit along its own line from the pivot.
  //   slots   {slots:[[x,y],…], charmScale}
  //           fixed coordinates that never redistribute; slots fill left to
  //           right and the leftovers stay empty.
  //
  // Any coordinate point takes an optional third value: rotation in degrees,
  // default 0. charmRotate scales how far each charm tilts with the chain in
  // curve mode: 1 = fully tangent, 0 = every charm dead upright.
  // charmTilt (degrees, default 0, all modes) is a fixed rotation added to every
  // charm on top of its position angle — offsets photos shot at an angle.
  function anchorNum(v, fallback) { return typeof v === "number" && isFinite(v) ? v : fallback; }
  // A point is [x, y] with an optional third rotation value.
  function anchorPt(p) {
    return (Object.prototype.toString.call(p) === "[object Array]" && p.length >= 2 &&
      isFinite(p[0]) && isFinite(p[1])) ? p : null;
  }
  function ptRot(p) { return p && isFinite(p[2]) ? Number(p[2]) : 0; }
  function defaultAnchor() {
    return { mode: "curve", start: DEFAULT_ANCHOR.start, end: DEFAULT_ANCHOR.end, sag: DEFAULT_ANCHOR.sag,
      charmScale: DEFAULT_ANCHOR.charmScale, charmRotate: DEFAULT_ANCHOR.charmRotate, capacity: null };
  }
  // Anything missing or unreadable degrades to the default curve arc with a
  // warning, exactly as before — a broken anchor must never blank the preview.
  function normalizeAnchor(raw, handle, kind) {
    var a = raw;
    if (typeof a === "string") { try { a = JSON.parse(a); } catch (e) { a = null; } }
    function bail(why) {
      console.warn('Charm builder: ' + kind + ' "' + handle + '" ' + why + ' — using the default curve arc.');
      return defaultAnchor();
    }
    if (!a || typeof a !== "object") return bail("has no readable custom.builder_anchor");
    var mode = String(a.mode == null ? "curve" : a.mode).trim().toLowerCase();
    var scale = anchorNum(a.charmScale, DEFAULT_ANCHOR.charmScale);
    var rotate = anchorNum(a.charmRotate, DEFAULT_ANCHOR.charmRotate);
    var tilt = anchorNum(a.charmTilt, 0);
    // A declared max only counts if it is a positive number.
    var max = anchorNum(a.max, 0) > 0 ? Math.floor(a.max) : null;

    if (mode === "slots") {
      var slots = [];
      if (Object.prototype.toString.call(a.slots) === "[object Array]") {
        a.slots.forEach(function (p) { var ok = anchorPt(p); if (ok) slots.push(ok); });
      }
      if (!slots.length) return bail('is mode "slots" but has no readable slots array');
      // slots.length IS the capacity; a stray "max" alongside it is ignored.
      return { mode: "slots", slots: slots, charmScale: scale, charmRotate: rotate, charmTilt: tilt, capacity: slots.length };
    }
    if (mode === "cluster") {
      var center = anchorPt(a.center);
      if (!center) return bail('is mode "cluster" but has no readable center point');
      return { mode: "cluster", center: center, gap: anchorNum(a.gap, 9),
        charmScale: scale, charmRotate: rotate, charmTilt: tilt, capacity: max };
    }
    if (mode === "fan") {
      var pivot = anchorPt(a.pivot);
      if (!pivot) return bail('is mode "fan" but has no readable pivot point');
      return { mode: "fan", pivot: pivot, spread: anchorNum(a.spread, 46), radius: anchorNum(a.radius, 13),
        charmScale: scale, charmRotate: rotate, charmTilt: tilt, capacity: max };
    }
    if (mode !== "curve") console.warn('Charm builder: ' + kind + ' "' + handle + '" has unknown anchor mode "' + mode + '" — treating it as curve.');
    var s = anchorPt(a.start), e = anchorPt(a.end);
    if (!s || !e) return bail("has no readable start/end points");
    return { mode: "curve", start: s, end: e, sag: anchorNum(a.sag, DEFAULT_ANCHOR.sag),
      charmScale: scale, charmRotate: rotate, charmTilt: tilt, capacity: max };
  }

  // How many charms the SELECTED base product holds. Declared capacity wins
  // outright; MAX_CHARMS is only the no-declaration fallback.
  function capacityOf(chain) {
    var cap = chain && chain.anchor ? chain.anchor.capacity : null;
    return cap && cap > 0 ? cap : MAX_CHARMS;
  }
  function cap() { return capacityOf(findChain(state.chainId)); }

  var CHAINS = (DATA.chains || []).map(function (c) {
    var anchor = normalizeAnchor(c.anchor, c.handle, "base product");
    if (!c.image) console.warn('Charm builder: chain "' + c.handle + '" has no builder_base_image or featured image.');
    return { id: String(c.id), handle: c.handle, title: c.title, variantId: c.variantId,
      price: c.price, priceLabel: c.priceLabel, available: !!c.available,
      metal: (c.metal || "").trim(), image: c.image, baseImage: c.image, anchor: anchor,
      compareAt: c.compareAt, compareAtLabel: c.compareAtLabel || null,
      onSale: c.compareAt != null && c.compareAt > c.price && !!c.compareAtLabel };
  });

  // Charms come from sections/cb-charm-data.liquid over the Section Rendering
  // API: paginate lifts the 50-item cap a plain collection loop is stuck with,
  // and metafields plus inventory quantities survive the trip. Parsed once and
  // cached here, so switching categories never refetches.

/* ==========================================================================
   CHARM DATA
   Fetched from sections/cb-charm-data.liquid, normalized once, then cached.
   ========================================================================== */

  var CHARMS = [];
  var charmsLoading = true, charmsError = false;

  // Turns the section's raw JSON into the shape the UI wants: stock collapsed
  // to a single number per variant, option charms given a uniform structure,
  // and category-less products dropped with a console warning naming them.
  function normalizeCharms(list) {
    var sale = list.filter(function (m) { return m.compareAtLabel; }).length;
    if (!sale) console.info("Charm builder: no charm in this page of data has a compare-at price above its price — nothing will show as on sale.");
    return list.reduce(function (out, m) {
      if (m.skip) {
        console.warn('Charm builder: charm "' + m.handle + '" has no custom.charm_category — skipped.');
        return out;
      }
      if (!m.charmImage) console.warn('Charm builder: charm "' + m.handle + '" has no custom.builder_charm_image — using its featured image in the grid.');
      var options = null;
      if (m.options && m.options.values && m.options.values.length) {
        options = { name: m.options.name || "Option", values: m.options.values.map(function (v) {
          return { value: v.value, variantId: v.variantId, charmImage: v.charmImage || null,
            priceLabel: v.priceLabel || null, compareAtLabel: v.compareAtLabel || null,
            onSale: !!v.compareAtLabel,
            stock: v.available ? Math.max(0, v.inventoryQuantity || 0) : 0 };
        }) };
      }
      out.push({ id: String(m.id), handle: m.handle, title: m.title, price: m.price,
        priceLabel: m.priceLabel, available: !!m.available, category: (m.category || "").trim(),
        metal: (m.metal || "").trim(), blurb: m.blurb || "", image: m.image, charmImage: m.charmImage,
        compareAtLabel: m.compareAtLabel || null,
        // Card-level sale flag: every variant discounted, or a single-variant
        // charm that is. A partial sale is deliberately not flagged here.
        onSale: !!m.compareAtLabel && (m.variantCount || 1) === (m.saleVariants || 0),
        variantId: m.variantId, stock: m.available ? Math.max(0, m.inventoryQuantity || 0) : 0, options: options });
      return out;
    }, []);
  }

  function fetchCharmPage(page) {
    return fetch("/?section_id=" + CHARM_SECTION + (page > 1 ? "&page=" + page : ""), { credentials: "same-origin" })
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.text();
      })
      .then(function (markup) {
        var doc = new DOMParser().parseFromString(markup, "text/html");
        var node = doc.querySelector("#cb-charm-data");
        if (!node) throw new Error("data script not found in section markup");
        return JSON.parse(node.textContent);
      });
  }

  function fetchAllCharms() {
    return fetchCharmPage(1).then(function (first) {
      var pages = Math.max(1, first.pages || 1);
      var rest = [];
      for (var p = 2; p <= pages; p++) rest.push(fetchCharmPage(p));
      return Promise.all(rest).then(function (others) {
        return (first.charms || []).concat.apply(first.charms || [], others.map(function (o) { return o.charms || []; }));
      });
    }).then(normalizeCharms);
  }

  // First CATEGORY_ORDER entry that has an available charm, so reordering the
  // list moves the default with it. Unlisted categories only win as a fallback.
  function defaultCategory() {
    function has(cat) { return CHARMS.some(function (c) { return c.category === cat && c.available; }); }
    for (var i = 0; i < CATEGORY_ORDER.length; i++) if (has(CATEGORY_ORDER[i])) return CATEGORY_ORDER[i];
    var any = CHARMS.filter(function (c) { return c.available; })[0];
    return any ? any.category : null;
  }
  function loadCharms() {
    fetchAllCharms().then(function (all) {
      CHARMS = all;
      charmsLoading = false;
      computeMetals();
      if (!state.category) state.category = defaultCategory();
      if (!CHARMS.length) console.warn("Charm builder: the charm data section returned no usable charms.");
      render();
    }).catch(function (err) {
      charmsLoading = false;
      charmsError = true;
      console.warn("Charm builder: could not load charm data from /?section_id=" + CHARM_SECTION, err);
      render();
    });
  }

  function findCharm(id) { for (var i = 0; i < CHARMS.length; i++) if (CHARMS[i].id === id) return CHARMS[i]; return null; }
  function findChain(id) { for (var i = 0; i < CHAINS.length; i++) if (CHAINS[i].id === id) return CHAINS[i]; return null; }

  var METALS = [], SHOW_METAL = false;
  function computeMetals() {
    METALS = [];
    CHARMS.concat(CHAINS).forEach(function (x) { if (x.metal && METALS.indexOf(x.metal) === -1) METALS.push(x.metal); });
    SHOW_METAL = METALS.length > 1;
  }


/* ==========================================================================
   STYLE TOKENS
   Shared inline-style strings. Everything is inline so the block owns no CSS beyond the <style> in the Liquid.
   ========================================================================== */

  var CARD_BASE = "appearance:none;text-align:left;cursor:pointer;font:inherit;color:inherit;background:#FFFFFF;border-radius:3px;display:flex;flex-direction:column;transition:border-color 140ms ease, box-shadow 140ms ease;";
  var S_STEP = "display:flex;flex-direction:column;gap:18px;";
  var S_HEAD = "display:flex;flex-wrap:wrap;align-items:baseline;gap:6px 16px;border-bottom:1px solid rgba(23,23,23,0.12);padding-bottom:12px;";
  var S_NUM = "font-size:var(--cb-fs-meta);letter-spacing:0.14em;color:#A3A3A3;font-variant-numeric:tabular-nums;";
  var S_H2 = "margin:0;font-family:var(--cb-font-heading);font-size:var(--cb-fs-step);font-weight:500;letter-spacing:0;white-space:nowrap;";
  var S_RIGHT = "margin-left:auto;font-size:var(--cb-fs-meta);letter-spacing:0.12em;text-transform:uppercase;color:#737373;text-align:right;";
  var S_BODY = "margin:0;font-size:var(--cb-fs-body);line-height:1.65;color:#525252;text-wrap:pretty;";
  var S_LINE = "display:flex;align-items:baseline;justify-content:space-between;gap:16px;padding:12px 0;border-bottom:1px solid rgba(23,23,23,0.08);";
  var S_LBL = "font-size:var(--cb-fs-label);letter-spacing:0.2em;text-transform:uppercase;";
  var S_NAME = "font-family:var(--cb-font-heading);font-size:var(--cb-fs-name);line-height:1.3;";
  var S_PRICE = "font-size:var(--cb-fs-price);color:#525252;font-variant-numeric:tabular-nums;";

  var S_HELPPRICE = "font-size:var(--cb-fs-helper);color:#525252;font-variant-numeric:tabular-nums;";
  var S_PANELTITLE = "font-family:var(--cb-font-heading);font-size:var(--cb-fs-panel-title);line-height:1.1;";
  var S_OPTNAME = "font-size:var(--cb-fs-label);letter-spacing:0.2em;text-transform:uppercase;color:#737373;";
  var S_HELPP = "margin:0;font-size:var(--cb-fs-helper);line-height:1.6;color:#525252;text-wrap:pretty;";
  var S_WRAPROW = "display:flex;flex-wrap:wrap;gap:8px;";

  var UID = 0;
  function nextUid() { return "i" + (++UID); }
  function esc(s) {
    return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }
  function head(num, title, right) {
    return '<div style="' + S_HEAD + '"><span style="' + S_NUM + '">' + num + '</span>' +
      '<h2 style="' + S_H2 + '">' + title + '</h2>' +
      '<span style="' + S_RIGHT + '">' + esc(right) + '</span></div>';
  }
  // Rendered inside the card <button>, so the controls are role="button" spans
  // rather than nested <button>s. Click delegation resolves the innermost
  // [data-cb], so a tap on −/+ never falls through to "add another".
  function stepper(c, count, addBlocked) {
    if (count < 1) return "";
    var d = 28;
    var btn = "width:" + d + "px;height:" + d + "px;border-radius:999px;display:flex;align-items:center;justify-content:center;" +
      "font-size:var(--cb-fs-helper);line-height:1;cursor:pointer;user-select:none;";
    return '<span style="display:flex;align-items:center;justify-content:space-between;gap:2px;' +
      'border:1px solid #171717;border-radius:999px;padding:1px;margin:0 2px 2px;">' +
      '<span role="button" tabindex="-1" aria-label="Remove one ' + esc(c.title) + '" data-cb="qty-dec" data-cb-id="' + esc(c.id) + '" style="' + btn + 'color:#171717;">\u2212</span>' +
      '<span style="font-size:var(--cb-fs-meta);font-variant-numeric:tabular-nums;min-width:12px;text-align:center;">' + count + '</span>' +
      '<span role="button" tabindex="-1" aria-label="Add another ' + esc(c.title) + '" data-cb="qty-inc" data-cb-id="' + esc(c.id) + '"' +
      (addBlocked ? ' data-cb-blocked="1"' : '') + ' style="' + btn + 'color:' + (addBlocked ? "#C4C4C4" : "#171717") + ';' +
      (addBlocked ? "cursor:not-allowed;" : "") + '">+</span></span>';
  }
  // cb- classes and inline styles on purpose. This used to reuse the theme's
  // price markup (.price__current / .price__was), and the theme's product JS
  // rewrites every such node on the page with the page product's own price once
  // it boots — which is why every chain card showed one shared figure until a
  // re-render replaced the nodes. Live price first, compare-at struck after it.
  function priceBlock(c) {
    var on = !!(c.onSale && c.compareAtLabel);
    return '<div class="cb-price" style="display:flex;flex-direction:column;align-items:flex-start;gap:2px;margin:0;font-variant-numeric:tabular-nums;">' +
      '<div style="display:flex;align-items:baseline;gap:5px;flex-wrap:wrap;">' +
      '<span class="cb-price-now" style="font-size:var(--cb-fs-price);line-height:1.3;color:#171717;">' + esc(c.priceLabel) + '</span>' +
      (on ? '<s class="cb-price-was" style="font-size:var(--cb-fs-label);line-height:1.3;color:#737373;">' + esc(c.compareAtLabel) + '</s>' : '') + '</div>' +
      (on ? '<span class="cb-price-sale" style="font-size:var(--cb-fs-nano);letter-spacing:.12em;text-transform:uppercase;line-height:1.3;color:#171717;">Sale</span>' : '') + '</div>';
  }
  function imgBg(url, extra, size) {
    return extra + (url ? "background-image:url('" + url + "');background-size:" + (size || "contain") + ";background-repeat:no-repeat;background-position:center;" : "");
  }


/* ==========================================================================
   STATE
   One object. setState(patch) merges and re-renders — that is the whole loop.
   ========================================================================== */

  // cart: "idle" | "busy" | "done" | "error" drives step 03's bottom section.
  // build: ordered array of instances, first charm at left first. Each is
  //   { uid, charmId, value (option or null), variantId }.
  // mobile = viewport below the theme's own 750px breakpoint (media queries
  // always measure the viewport). Only this flag enables the sticky header and
  // the gallery hide; `wide` only tunes grid density.
  var MQ_MOBILE = window.matchMedia("(max-width: 749px)");
  var state = { chainId: null, wide: window.matchMedia("(min-width: 1000px)").matches, mobile: MQ_MOBILE.matches,
    build: [], modalId: null, category: null, metal: null, editUid: null,
    dragIndex: null, overIndex: null, mounted: false, aspect: FALLBACK_ASPECT,
    cart: "idle", cartMsg: null, buildId: null, chainOpen: false, trimNote: null };

  var portal = root.querySelector("[data-cb-portal]");
  var stepsEl = root.querySelector("[data-cb-steps]");
  var modalRoot = root.querySelector("[data-cb-modal-root]");
  // The mobile sticky header is fixed to the viewport, so it lives on <body> —
  // a transformed ancestor in the theme would otherwise break position:fixed.
  var stale = document.querySelector("[data-cb-bar-root]");
  if (stale) stale.remove();
  var barRoot = document.createElement("div");
  barRoot.setAttribute("data-cb-bar-root", "");
  document.body.appendChild(barRoot);
  function setState(patch) { Object.assign(state, patch); render(); }


/* ==========================================================================
   BUILD MUTATIONS
   Every one enforces the 10-charm cap and per-variant stock before mutating.
   ========================================================================== */

  // Counts instances of one specific variant, which is what stock is measured
  // against — two different letters of the same charm are separate variants.
  function countIn(charmId, value) {
    return state.build.filter(function (b) { return b.charmId === charmId && b.value === value; }).length;
  }
  // Any edit to the build invalidates a shown success/error panel.
  function touchBuild() { if (state.cart !== "busy") { state.cart = "idle"; state.cartMsg = null; } state.trimNote = null; }
  var CAP_WORDS = ["zero","one","two","three","four","five","six","seven","eight","nine","ten"];
  function spellCap(n) { return CAP_WORDS[n] || n; }
  // Switching base product keeps the first N charms in hang order and drops the
  // rest: never clear the build, never block the switch, never prompt.
  function switchChain(ch) {
    var limit = capacityOf(ch), note = null;
    if (state.build.length > limit) {
      var dropped = state.build.length - limit;
      state.build = state.build.slice(0, limit);
      note = dropped === 1
        ? "One charm came off — this one holds " + spellCap(limit) + "."
        : spellCap(dropped) + " charms came off — this one holds " + spellCap(limit) + ".";
    }
    touchBuild();
    setState({ chainId: ch.id, chainOpen: false, trimNote: note });
  }
  function addSimple(c) {
    if (state.build.length >= cap() || countIn(c.id, null) >= c.stock) return;
    state.build = state.build.concat([{ uid: nextUid(), charmId: c.id, value: null, variantId: c.variantId }]);
    touchBuild();
    render();
  }
  // Stepper "−" drops the most recently added instance of the charm, whatever
  // variant it is — matching what the chip row's ✕ does for a single instance.
  function removeLast(charmId) {
    for (var i = state.build.length - 1; i >= 0; i--) {
      if (state.build[i].charmId === charmId) {
        state.build = state.build.slice(0, i).concat(state.build.slice(i + 1));
        touchBuild();
        render();
        return;
      }
    }
  }
  function addVariant(c, opt) {
    if (state.build.length >= cap() || countIn(c.id, opt.value) >= opt.stock) return;
    state.build = state.build.concat([{ uid: nextUid(), charmId: c.id, value: opt.value, variantId: opt.variantId }]);
    touchBuild();
    setState({ modalId: null });
  }
  function removeUid(uid) {
    state.build = state.build.filter(function (b) { return b.uid !== uid; });
    touchBuild();
    setState({ editUid: state.editUid === uid ? null : state.editUid });
  }
  function replaceVariant(uid, opt) {
    state.build = state.build.map(function (b) {
      return b.uid === uid ? Object.assign({}, b, { value: opt.value, variantId: opt.variantId }) : b;
    });
    touchBuild();
    setState({ editUid: null });
  }
  function move(from, to) {
    if (from == null || to == null || to < 0 || to >= state.build.length || from === to) {
      setState({ dragIndex: null, overIndex: null }); return;
    }
    var next = state.build.slice();
    next.splice(to, 0, next.splice(from, 1)[0]);
    state.build = next;
    setState({ dragIndex: null, overIndex: null });
  }

  // Pointer reorder — mouse, touch and pen alike (HTML5 drag never fires on
  // touch). Arrow buttons are desktop only; phones long-press a row instead.

/* ==========================================================================
   REORDER
   Pointer events, not HTML5 drag — the latter never fires on touch.
   ========================================================================== */

  var moveHandler = null, endHandler = null, suppressClickUntil = 0;

  // Phone rows grab on a long press (320ms without moving more than 8px), so a
  // short press or swipe still scrolls the page. Once grabbed, touchmove is
  // cancelled so the page stays put while the row follows the finger.
  var press = null;
  function cancelPress() {
    if (!press) return;
    clearTimeout(press.timer);
    document.removeEventListener("pointermove", press.move);
    document.removeEventListener("pointerup", cancelPress);
    document.removeEventListener("pointercancel", cancelPress);
    press = null;
  }
  function blockTouch(ev) { if (state.dragIndex != null) ev.preventDefault(); }
  function startPress(i, e) {
    cancelPress();
    var x0 = e.clientX, y0 = e.clientY, tgt = e.target;
    // Touch events keep targeting the original node even after a re-render
    // detaches it, so the blocker has to live on that node, not an ancestor.
    tgt.addEventListener("touchmove", blockTouch, { passive: false });
    tgt.addEventListener("touchend", function off() {
      tgt.removeEventListener("touchmove", blockTouch); tgt.removeEventListener("touchend", off);
    });
    press = { move: function (ev) { if (Math.abs(ev.clientX - x0) > 8 || Math.abs(ev.clientY - y0) > 8) cancelPress(); } };
    press.timer = setTimeout(function () {
      cancelPress();
      if (navigator.vibrate) { try { navigator.vibrate(12); } catch (err) {} }
      onGrab(i, e, null);
    }, 320);
    document.addEventListener("pointermove", press.move);
    document.addEventListener("pointerup", cancelPress);
    document.addEventListener("pointercancel", cancelPress);
  }
  barRoot.addEventListener("touchmove", blockTouch, { passive: false });
  barRoot.addEventListener("contextmenu", function (ev) { if (ev.target.closest && ev.target.closest("[data-cb-row]")) ev.preventDefault(); });
  // No render() during a drag: the grabbed node is moved within its own list
  // directly, so the preview, header height and page stay untouched. The new
  // order is read back from the DOM and committed with one render on release.
  function onGrab(i, e, handle) {
    if (e.button != null && e.button > 0) return;
    e.preventDefault(); e.stopPropagation();
    var src = handle || e.target;
    var el = src && src.closest ? src.closest("[data-cb-chip]") : null;
    var scope = el && el.parentNode;
    if (!scope) return;
    state.dragIndex = i; state.overIndex = i;
    var saved = el.style.cssText;
    el.style.cssText = saved + (el.hasAttribute("data-cb-row")
      ? "background:#171717;color:#FFFFFF;border-color:#171717;box-shadow:0 6px 16px rgba(23,23,23,0.25);position:relative;z-index:1;touch-action:none;"
      : "opacity:0.4;touch-action:none;");
    var scroller = el.closest(".cb-rows");
    if (handle && handle.setPointerCapture) { try { handle.setPointerCapture(e.pointerId); } catch (err) {} }
    moveHandler = function (ev) {
      if (scroller) {
        var r = scroller.getBoundingClientRect();
        if (ev.clientY < r.top + 18) scroller.scrollTop -= 6;
        else if (ev.clientY > r.bottom - 18) scroller.scrollTop += 6;
      }
      var hit = document.elementFromPoint(ev.clientX, ev.clientY);
      var t = hit && hit.closest ? hit.closest("[data-cb-chip]") : null;
      if (!t || t === el || t.parentNode !== scope) return;
      var kids = Array.prototype.slice.call(scope.children);
      scope.insertBefore(el, kids.indexOf(t) > kids.indexOf(el) ? t.nextSibling : t);
    };
    endHandler = function () {
      document.removeEventListener("pointermove", moveHandler);
      document.removeEventListener("pointerup", endHandler);
      document.removeEventListener("pointercancel", endHandler);
      document.removeEventListener("touchmove", blockTouch, { passive: false });
      moveHandler = null; endHandler = null;
      suppressClickUntil = Date.now() + 400;
      var order = [], same = true;
      Array.prototype.forEach.call(scope.children, function (n) {
        if (!n.hasAttribute("data-cb-chip")) return;
        var k = Number(n.getAttribute("data-cb-chip"));
        if (k !== order.length) same = false;
        order.push(k);
      });
      if (same || order.length !== state.build.length) {
        el.style.cssText = saved; state.dragIndex = null; state.overIndex = null; return;
      }
      var prev = state.build;
      state.build = order.map(function (k) { return prev[k]; });
      setState({ dragIndex: null, overIndex: null });
    };
    document.addEventListener("touchmove", blockTouch, { passive: false });
    document.addEventListener("pointermove", moveHandler);
    document.addEventListener("pointerup", endHandler);
    document.addEventListener("pointercancel", endHandler);
  }


/* ==========================================================================
   GALLERY MOUNT
   Moves the preview node into the theme gallery and keeps it there.
   ========================================================================== */

  // This is the fiddliest part of the file, and all of the fiddliness is the
  // theme's: the slider marks its active slide only after its own JS runs, it
  // rebuilds gallery nodes per breakpoint, and the Personalizer app injects a
  // canvas into the very same node. So we retry on a schedule, watch for DOM
  // changes, and re-validate the chosen target on resize.
  var mountTarget = null, galleryWrap = null, galleryKey = null,
      observer = null, observedRoot = null, remountTimer = null, warnedMount = false;
  function isVisible(el) {
    if (!el || !el.isConnected) return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }
  function findTarget() {
    for (var i = 0; i < MOUNT_SELECTORS.length; i++) {
      var hits = document.querySelectorAll(MOUNT_SELECTORS[i]);
      for (var j = 0; j < hits.length; j++) {
        if (hits[j].closest(".pplr_preview_wrapper")) continue;
        if (isVisible(hits[j])) return hits[j];
      }
    }
    return null;
  }
  function readAspect(el) {
    var n = parseFloat(getComputedStyle(el).getPropertyValue("--aspect-ratio"));
    return isFinite(n) && n > 0 ? n : FALLBACK_ASPECT;
  }
  function findGalleryWrap(t) {
    // Outermost gallery/slider/media ancestor of the mount target that does not
    // also contain the builder itself — no reliance on a thumbnail strip.
    var RX = /(media-gallery|product__media|product-media|product-gallery|slider|gallery)/i;
    var best = null, el = t.parentElement;
    for (var i = 0; i < 12 && el && el !== document.body; i++, el = el.parentElement) {
      if (el.contains(root)) break;
      var sig = el.tagName + " " + (typeof el.className === "string" ? el.className : "") + " " + (el.id || "");
      if (RX.test(sig)) best = el;
    }
    var picked = best || t.closest(".product-media-wrapper") || t.parentElement;
    if (picked && picked.contains(root)) picked = t.parentElement;
    if (window.console) console.info("Charm builder: gallery wrapper →", picked && (picked.tagName.toLowerCase() + (picked.id ? "#" + picked.id : "") + (typeof picked.className === "string" && picked.className ? "." + picked.className.trim().split(/\s+/).join(".") : "")), picked);
    return picked;
  }
  function mountPreview() {
    var target = findTarget();
    if (!target) {
      if (state.mounted) setState({ mounted: false });
      return false;
    }
    if (portal.parentElement !== target) { target.classList.add("cb-mount-host"); target.appendChild(portal); }
    mountTarget = target;
    observeSlider(target);
    var aspect = readAspect(target);
    if (!state.mounted || state.aspect !== aspect) setState({ mounted: true, aspect: aspect });
    applyGalleryState();
    return true;
  }

  // The theme's slider adds .is-active after its own JS runs, so the first
  // attempt almost always misses. Retry on a schedule, plus a body observer
  // for a late-rendering gallery; give up (and warn once) after ~6s.
  var pollTimer = null, bodyObserver = null, pollStart = 0, lastTry = 0;
  function attemptMount() {
    var now = Date.now();
    if (now - lastTry < 120) return;                 // throttle observer bursts
    lastTry = now;
    if (state.mounted && portal.isConnected) { stopRetry(); return; }
    if (mountPreview()) stopRetry();
  }
  function stopRetry() {
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    if (bodyObserver) { bodyObserver.disconnect(); bodyObserver = null; }
  }
  function startRetry() {
    if (pollTimer) return;
    pollStart = Date.now();
    pollTimer = setInterval(function () {
      if (Date.now() - pollStart > 6000) {
        stopRetry();
        if (!state.mounted && !warnedMount) {
          warnedMount = true;
          console.warn("Charm builder: product media mount target not found after retries — rendering the preview inline.");
        }
        return;
      }
      attemptMount();
    }, 300);
    if (document.body && !bodyObserver) {
      bodyObserver = new MutationObserver(attemptMount);
      bodyObserver.observe(document.body, { childList: true, subtree: true });
    }
  }
  function observeSlider(target) {
    var slide = target.closest(".slider__item");
    var obsRoot = slide ? slide.parentElement : target.parentElement;
    if (!obsRoot || observedRoot === obsRoot) return;
    if (observer) observer.disconnect();
    observedRoot = obsRoot;
    observer = new MutationObserver(function () {
      if (remountTimer) return;                    // debounce: one remount per tick window
      remountTimer = setTimeout(function () {
        remountTimer = null;
        var next = findTarget();
        if (!portal.isConnected || (next && portal.parentElement !== next)) mountPreview();
        else applyGalleryState();
      }, 200);
    });
    observer.observe(obsRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ["class"] });
  }
  function applyGalleryState() {
    var on = !!state.chainId && state.mounted;
    if (!mountTarget) return;
    // The theme swaps gallery nodes per breakpoint; re-resolve if the cached
    // wrapper is detached or no longer holds the current mount target.
    if (galleryWrap && (!galleryWrap.isConnected || !galleryWrap.contains(mountTarget))) {
      galleryWrap.classList.remove("cb-media-hidden"); galleryWrap = null; galleryKey = null;
    }
    galleryWrap = galleryWrap || findGalleryWrap(mountTarget);
    if (!galleryWrap) return;
    // No latch: toggling is idempotent, so re-apply on every call (render,
    // mount, remount, theme DOM mutation) in case the theme reset classes.
    // Same on every breakpoint: the preview fills the gallery box, the theme's
    // media only fades, so page height never changes.
    galleryKey = on;
    if (galleryWrap.classList.contains("cb-media-hidden") !== on) galleryWrap.classList.toggle("cb-media-hidden", on);
    galleryWrap.querySelectorAll('button[class*="slider-button"], [class*="slider-buttons"] button')
      .forEach(function (b) { b.disabled = on; });
  }


/* ==========================================================================
   MARKUP HELPERS
   Small string builders shared by render() and renderModal().
   ========================================================================== */

  function grip(small, inert) {
    var d = small ? 2.5 : 3;
    return '<span aria-hidden="true"' + (inert ? '' : ' role="button" aria-label="Drag to reorder" data-cb="grab"') + ' style="display:grid;grid-template-columns:repeat(2, ' + d + 'px);gap:' + (small ? 2 : 3) + 'px;padding:' + (small ? "5px 3px" : "6px 4px") + ';margin:' + (small ? "-5px 0 -5px -1px" : "-6px 0 -6px -2px") + ';' + (inert ? '' : 'cursor:grab;touch-action:none;') + '">' +
      new Array(7).join('<span style="width:' + d + 'px;height:' + d + 'px;border-radius:50%;background:#A3A3A3;"></span>') + '</span>';
  }
  var GRIP = grip(false);

  function chipStyle(on) {
    return "appearance:none;font:inherit;cursor:pointer;display:flex;align-items:center;gap:8px;padding:9px 14px;" +
      "border-radius:999px;white-space:nowrap;flex:none;font-size:var(--cb-fs-chip);transition:border-color 120ms ease, background 120ms ease;" +
      "border:1px solid " + (on ? "#171717" : "rgba(23,23,23,0.18)") + ";background:" + (on ? "#171717" : "#FFFFFF") +
      ";color:" + (on ? "#FFFFFF" : "#171717") + ";";
  }

  function optionsHtml(charm, editItem, action) {
    var atCap = state.build.length >= cap();
    return (charm.options ? charm.options.values : []).map(function (o) {
      var inBuild = countIn(charm.id, o.value);
      var current = editItem && editItem.value === o.value;
      var held = current ? inBuild - 1 : inBuild;      // swapping frees this instance's unit
      var soldOut = o.stock <= 0;
      var maxed = !soldOut && held >= o.stock;          // never more instances than inventory
      var off = soldOut || maxed || (atCap && !editItem);
      var note = soldOut ? "Sold out" : maxed ? "No more available" : current ? "Current" : "";
      // A charm whose variants are only partly discounted shows nothing on the
      // card, so the per-letter flag here is the only place the sale appears.
      var optSale = o.onSale && !soldOut && !note;
      var style = "appearance:none;font:inherit;min-width:52px;min-height:44px;padding:6px 14px;border-radius:2px;" +
        "display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;background:#FFFFFF;color:#171717;" +
        "transition:border-color 120ms ease, background 120ms ease;border:1px solid " +
        (current ? "#171717" : "rgba(23,23,23,0.18)") + ";" +
        (soldOut ? "opacity:0.35;cursor:not-allowed;text-decoration:line-through;" : off ? "opacity:0.4;cursor:not-allowed;" : "cursor:pointer;");
      return '<button type="button" class="cb-opt" data-cb="' + action + '" data-cb-charm="' + esc(charm.id) +
        '" data-cb-val="' + esc(o.value) + '"' + (off ? ' data-cb-blocked="1"' : '') + ' style="' + style + '">' +
        '<span style="font-size:var(--cb-fs-helper);">' + esc(o.value) + '</span>' +
        (note ? '<span style="font-size:var(--cb-fs-nano);letter-spacing:0.1em;text-transform:uppercase;color:#737373;">' + esc(note) + '</span>' : '') +
        (optSale ? '<span class="price-label price-label--sale">Sale</span>' : '') +
        '</button>';
    }).join("");
  }

  function helperCopy(editItem) {
    if (editItem) return "Pick a different one and it swaps in place — it keeps its spot.";
    if (state.build.length >= cap()) return "You’re at " + cap() + (cap() === 1 ? " charm" : " charms") + " — remove one to make room.";
    return "Tap the one you want and it goes straight on. Tap it again for another.";
  }

  // One POST to /cart/add.js: the chain plus every charm instance as its own
  // quantity-1 line, so inventory deducts per product. Identical variants would
  // be merged by Shopify, which is why each charm line carries a distinct
  // _position — that also records hang order for the packing team.
  function makeBuildId() {
    var hex = "0123456789ABCDEF", s = "";
    for (var i = 0; i < 4; i++) s += hex.charAt(Math.floor(Math.random() * 16));
    return s;
  }
  // Cut-out shown in the visual preview for one build instance: the chosen
  // variant's own image when the charm has per-variant art (the letter fonts),
  // otherwise the product-level image. Cards are unaffected — they always show
  // the featured product media.
  function charmArt(c, b) {
    if (c && c.options && b && b.value != null) {
      var v = c.options.values.filter(function (o) { return o.value === b.value; })[0];
      if (v && v.charmImage) return v.charmImage;
    }
    return c ? c.charmImage : null;
  }
  function labelFor(b) {
    var c = findCharm(b.charmId);
    var t = c ? c.title : "Charm";
    return b.value ? t + " \u2014 " + b.value : t;
  }
  function stockIn(list, b) {
    var c = null;
    for (var i = 0; i < list.length; i++) if (list[i].id === b.charmId) { c = list[i]; break; }
    if (!c || !c.available) return 0;
    if (!c.options) return c.stock;
    var o = c.options.values.filter(function (v) { return v.value === b.value; })[0];
    return o ? o.stock : 0;
  }
  // Charms share stock with Shopee and TikTok Shop, so a build assembled a
  // minute ago can already be stale. Re-read the data section and compare
  // instance counts against live inventory before anything is posted.
  function firstProblem(fresh) {
    var seen = {}, problem = null;
    state.build.forEach(function (b) {
      if (problem) return;
      var key = b.charmId + "|" + b.value;
      if (seen[key]) return;
      seen[key] = true;
      var want = countIn(b.charmId, b.value);
      var have = stockIn(fresh, b);
      if (have <= 0) problem = labelFor(b) + " just sold out — remove it from your build and add to cart again.";
      else if (have < want) problem = "Only " + have + " of " + labelFor(b) + " " + (have === 1 ? "is" : "are") +
        " left, and your build has " + want + ". Remove " + (want - have) + " and add to cart again.";
    });
    return problem;
  }
  function checkChain(chain) {
    return fetch("/products/" + chain.handle + ".js", { credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p) return;
        var v = (p.variants || []).filter(function (x) { return String(x.id) === String(chain.variantId); })[0];
        if (v && v.available === false) throw new Error(chain.title + " just sold out — pick another chain and add to cart again.");
      });
  }
  function buildItems(chain, bid) {
    var items = [{ id: chain.variantId, quantity: 1 }];
    if (state.build.length) {
      items[0].properties = {
        "_build_id": bid,
        "_role": "chain",
        "Build": "#" + bid,
        "Charm order (left to right)": state.build.map(labelFor).join(", ")
      };
      state.build.forEach(function (b, i) {
        items.push({ id: b.variantId, quantity: 1, properties: {
          "_build_id": bid, "_role": "charm", "_position": String(i + 1), "Build": "#" + bid } });
      });
    }
    // The theme's cart lists the most recently added line first, so post the
    // build back-to-front: last charm added first, chain last. _position still
    // carries true hang order (1 = first charm at left) for the packing team.
    return items.reverse();
  }
  function addToCart() {
    if (state.cart === "busy") return;                 // double-submit guard
    var chain = findChain(state.chainId);
    if (!chain) return;
    setState({ cart: "busy", cartMsg: null });
    var bid = makeBuildId();
    fetchAllCharms()
      .then(function (fresh) {
        var problem = firstProblem(fresh);
        if (problem) throw new Error(problem);
        CHARMS = fresh;                                 // keep the grid honest too
        return checkChain(chain);
      })
      .then(function () {
        return fetch("/cart/add.js", {
          method: "POST", credentials: "same-origin",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ items: buildItems(chain, bid) })
        }).then(function (r) {
          return r.json().then(function (j) {
            if (!r.ok) throw new Error(j.description || j.message || ("Cart error " + r.status));
            return j;
          }, function () {
            if (!r.ok) throw new Error("Cart error " + r.status);
            return null;
          });
        });
      })
      .then(function () { setState({ cart: "done", buildId: bid, cartMsg: null }); })
      // Nothing partial ever lands: a single failed POST adds no lines at all,
      // and the build is left exactly as it was.
      .catch(function (err) {
        setState({ cart: "error", cartMsg: (err && err.message) || "That didn’t go through. Nothing was added to your cart — try again." });
      });
  }
  function startAnother() {
    state.build = [];
    setState({ chainId: null, category: defaultCategory(), buildId: null, cart: "idle", cartMsg: null, modalId: null, editUid: null, chainOpen: false, trimNote: null });
  }


/* ==========================================================================
   RENDER
   Builds all three steps as one HTML string. Steps 02 and 03 only exist once a chain is chosen.
   ========================================================================== */

  function render() {
    var selected = findChain(state.chainId);
    var atCap = state.build.length >= capacityOf(selected);
    var cols = 3;
    var pad = state.wide ? 10 : 6;
    var grid = "display:grid;grid-template-columns:repeat(" + cols + ",minmax(0,1fr));gap:" + (state.wide ? 12 : 8) + "px;";
    var barHtml = "";
    if (SHOW_METAL && !state.metal && selected && selected.metal) state.metal = selected.metal;

    // categories derive from the values actually present
    var counts = {};
    CHARMS.forEach(function (c) {
      if (counts[c.category] == null) counts[c.category] = 0;
      if (c.available) counts[c.category] += 1;
    });
    function rank(cat) {
      if (cat === "Others") return 10000;
      var i = CATEGORY_ORDER.indexOf(cat);
      return i === -1 ? 5000 : i;
    }
    var cats = Object.keys(counts).filter(function (c) { return counts[c] > 0; })
      .sort(function (a, b) { return rank(a) - rank(b) || a.localeCompare(b); });

    // Once a chain is chosen the picker folds away — the choice is made once,
    // and the charm grid is where the rest of the session happens. The summary
    // row keeps the decision visible and re-openable.
    var chainOpen = !state.chainId || state.chainOpen;
    var S_TOGGLE = "appearance:none;font:inherit;cursor:pointer;background:transparent;border:0;color:#737373;" +
      "font-size:var(--cb-fs-label);letter-spacing:0.16em;text-transform:uppercase;padding:8px;";

    var html = '<section class="cb-step" style="' + S_STEP + '">' +
      head("01", "SELECT YOUR BASE", selected ? selected.title : CHAINS.length + (CHAINS.length === 1 ? " base" : " bases"));

    if (!chainOpen) {
      html += '<div style="display:flex;align-items:center;gap:14px;border:1px solid rgba(23,23,23,0.14);border-radius:3px;padding:10px;background:#FFFFFF;">' +
        '<div style="' + imgBg(selected.image, "width:56px;flex:none;aspect-ratio:1/1;border-radius:2px;background-color:#F5F5F5;") + '"></div>' +
        '<div style="display:flex;flex-direction:column;gap:3px;min-width:0;">' +
        '<span style="' + S_NAME + '">' + esc(selected.title) + '</span>' +
        priceBlock(selected) + '</div>' +
        '<button type="button" class="cb-ghost" data-cb="toggle-chain" aria-expanded="false" style="margin-left:auto;' + S_TOGGLE + '">Change</button>' +
        '</div>';
    } else {
      html += '<p style="' + S_BODY + '">This is the piece that holds everything together, so take your time with it. Leave it empty and it wears beautifully on its own.</p>' +
        '<div class="cb-chain-grid" style="' + grid + '">';
      CHAINS.forEach(function (c) {
        html += '<button type="button" class="cb-card" data-cb="chain" data-cb-id="' + esc(c.id) + '"' +
          (c.available ? '' : ' data-cb-blocked="1"') + ' style="' + CARD_BASE + 'gap:12px;padding:10px;border:1px solid ' +
          (state.chainId === c.id ? "#171717" : "rgba(23,23,23,0.14)") + ';' + (c.available ? '' : 'opacity:0.45;cursor:not-allowed;') + '">' +
          '<div style="' + imgBg(c.image, "aspect-ratio:1/1;border-radius:2px;background-color:#F5F5F5;") + '"></div>' +
          '<div style="display:flex;flex-direction:column;gap:4px;padding:0 2px 2px;">' +
          '<span style="' + S_NAME + '">' + esc(c.title) + '</span>' +
          priceBlock(c) + '</div></button>';
      });
      html += '</div>';
      if (selected) {
        html += '<div style="display:flex;"><button type="button" class="cb-ghost" data-cb="toggle-chain" aria-expanded="true" style="' +
          S_TOGGLE + 'margin-left:-8px;">Done \u2014 hide chains</button></div>';
      }
    }
    html += '</section>';

    if (state.chainId) {
      var visible = CHARMS.filter(function (c) {
        if (state.category && c.category !== state.category) return false;
        if (SHOW_METAL && state.metal && c.metal && c.metal !== state.metal) return false;
        return true;
      });
      var modalCharm = state.modalId ? findCharm(state.modalId) : null;
      var openIndex = modalCharm ? visible.indexOf(modalCharm) : -1;
      var inlinePanel = state.wide && openIndex >= 0;
      var panelIndex = inlinePanel ? Math.min(Math.floor(openIndex / cols) * cols + cols - 1, visible.length - 1) : -1;

      html += '<section class="cb-step" style="' + S_STEP + '">' +
        head("02", "PICK YOUR CHARMS", state.build.length + (state.build.length === 1 ? " charm" : " charms")) +
        '<p style="' + S_BODY + '">' + (capacityOf(selected) === 1 ? 'Pick the charm you’d like.'
          : 'Add up to ' + spellCap(capacityOf(selected)) + ' charms, and repeat any of them as often as you like.') + '</p>';

      // Switching to a smaller base product silently drops the overflow, so say so.
      if (state.trimNote) {
        html += '<p class="cb-notice" style="margin:0;padding:12px 14px;border:1px solid rgba(23,23,23,0.28);border-radius:3px;font-size:var(--cb-fs-helper);line-height:1.5;">' +
          esc(state.trimNote) + '</p>';
      }

      if (SHOW_METAL) {
        html += '<div class="cb-metals" style="' + S_WRAPROW + '">';
        METALS.forEach(function (m) {
          html += '<button type="button" class="cb-chip" data-cb="metal" data-cb-val="' + esc(m) + '" style="' + chipStyle(state.metal === m) + '"><span>' + esc(m) + '</span></button>';
        });
        html += '</div>';
      }

      html += '<div class="cb-cats" style="' + S_WRAPROW + '">';
      cats.forEach(function (cat) {
        html += '<button type="button" class="cb-chip" data-cb="cat" data-cb-val="' + esc(cat) + '" style="' + chipStyle(state.category === cat) + '">' +
          '<span>' + esc(cat) + '</span><span style="font-size:var(--cb-fs-label);opacity:0.6;font-variant-numeric:tabular-nums;">' + counts[cat] + '</span></button>';
      });
      html += '</div>';

      if (charmsLoading) {
        html += '<p style="' + S_BODY + 'color:#A3A3A3;">Bringing out the charm tray…</p>';
      } else if (charmsError) {
        html += '<p class="cb-notice" style="margin:0;padding:14px 16px;border:1px solid #171717;border-radius:3px;font-size:var(--cb-fs-helper);line-height:1.5;">' +
          'The charm library didn’t load. Refresh the page — if it keeps happening, check that the <strong>cb-charm-data</strong> section exists in the theme.</p>';
      } else {
      html += '<div class="cb-charm-grid" style="' + grid + '">';

      visible.forEach(function (c, i) {
        var oos = !c.available;
        var simple = !c.options;
        var count = simple ? countIn(c.id, null) : state.build.filter(function (b) { return b.charmId === c.id; }).length;
        // Option charms are only maxed when every variant is out or at its own cap.
        var maxed = !oos && (simple ? count >= c.stock
          : c.options.values.every(function (o) { return o.stock <= 0 || countIn(c.id, o.value) >= o.stock; }));
        var blocked = oos || maxed || (atCap && !oos);
        var status = oos ? "Out of stock" : maxed ? "No more available" : atCap ? "Remove one to add another" : "";

        html += '<div style="display:contents;">' +
          '<button type="button" class="cb-card" data-cb="charm" data-cb-id="' + esc(c.id) + '"' + (blocked ? ' data-cb-blocked="1"' : '') +
          ' style="' + CARD_BASE + 'gap:' + (state.wide ? 10 : 7) + 'px;padding:' + pad + 'px;position:relative;transform:translateZ(0);' +
          'transition:border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease, opacity 160ms ease;border:1px solid ' +
          (count > 0 || (!simple && openIndex === i) ? "#171717" : "rgba(23,23,23,0.14)") + ';' +
          (blocked ? (count > 0 ? "cursor:default;" : "opacity:0.45;cursor:not-allowed;") : "") + '">' +
          '<div style="' + imgBg(c.image, "width:100%;aspect-ratio:1/1;border-radius:2px;background-color:#F5F5F5;overflow:hidden;", state.wide ? null : "120%") + '"></div>' +
          '<div style="display:flex;flex-direction:column;gap:3px;padding:0 2px' + (count > 0 ? '' : ' 2px') + ';">' +
          '<span style="' + S_NAME + '">' + esc(c.title) + '</span>' +
          priceBlock(c) +
          (status ? '<span style="font-size:var(--cb-fs-label);letter-spacing:0.14em;text-transform:uppercase;color:' + (maxed ? "#171717" : "#737373") + ';">' + esc(status) + '</span>' : '') +
          '</div>' + stepper(c, count, maxed || oos || atCap) + '</button>';

        if (inlinePanel && i === panelIndex) {
          var p = modalCharm;
          html += '<div class="cb-inline-panel" style="grid-column:1 / -1;border:1px solid #171717;border-radius:3px;background:#FFFFFF;padding:16px;display:flex;flex-wrap:wrap;gap:16px;align-items:flex-start;">' +
            '<div style="' + imgBg(p.image, "width:110px;flex:none;aspect-ratio:1/1;background-color:#F5F5F5;") + '"></div>' +
            '<div style="display:flex;flex-direction:column;gap:12px;min-width:0;flex:1;">' +
            '<div style="display:flex;align-items:baseline;gap:14px;">' +
            '<span style="' + S_PANELTITLE + '">' + esc(p.title) + '</span>' +
            '<span style="' + S_HELPPRICE + '">' + esc(p.priceLabel) + '</span>' +
            '<button type="button" class="cb-ghost" data-cb="panel-close" style="margin-left:auto;appearance:none;font:inherit;cursor:pointer;background:transparent;border:0;color:#737373;font-size:var(--cb-fs-label);letter-spacing:0.16em;text-transform:uppercase;padding:4px;">Close</button></div>' +
            '<p style="' + S_HELPP + '">' + esc(p.blurb) + ' ' + esc(helperCopy(null)) + '</p>' +
            '<span style="' + S_OPTNAME + '">' + esc(p.options ? p.options.name : "") + '</span>' +
            '<div style="' + S_WRAPROW + '">' + optionsHtml(p, null, "add-variant") + '</div></div></div>';
        }
        html += '</div>';
      });
      html += '</div>';
      }

      // Desktop only — on phones the reorder list lives in the sticky header.
      if (state.wide && capacityOf(selected) > 1 && !charmsLoading && !charmsError && (state.category || state.build.length)) {
      var o = '<div style="display:flex;align-items:baseline;gap:16px;">' +
        '<span style="' + S_LBL + 'font-weight:500;color:#171717;">Drag to reorder, or use \u2191 \u2193 to move an item</span>' +
        '<span style="margin-left:auto;font-size:var(--cb-fs-label);letter-spacing:0.14em;text-transform:uppercase;color:#A3A3A3;">First charm at left</span></div>';
      if (state.build.length) {
        o += '<div class="cb-chip-row" style="' + S_WRAPROW + '">';
        state.build.forEach(function (b, i) {
          var c = findCharm(b.charmId);
          var dragging = state.dragIndex === i;
          var over = state.overIndex === i && state.dragIndex !== null && state.dragIndex !== i;
          o += '<div class="cb-chip" data-cb-chip="' + i + '" data-cb="chip-edit" style="' +
            'font:inherit;font-size:var(--cb-fs-chip);color:#171717;display:flex;align-items:center;gap:10px;padding:8px 10px 8px 8px;' +
            'border-radius:999px;background:#FFFFFF;' +
            'transition:border-color 120ms ease, opacity 120ms ease, box-shadow 120ms ease;border:1px solid ' +
            (over || state.editUid === b.uid ? "#171717" : "rgba(23,23,23,0.18)") + ';' +
            (over ? "box-shadow:inset 3px 0 0 #171717;" : "") + (dragging ? "opacity:0.4;" : "") +
            (state.dragIndex != null ? "touch-action:none;" : "") + '">' + GRIP +
            '<span>' + esc(b.value ? c.title + " \u2014 " + b.value : c.title) + '</span>' +
            '<span style="display:flex;gap:2px;">' +
            '<button type="button" data-cb="up" aria-label="Move earlier" style="appearance:none;font:inherit;cursor:pointer;background:transparent;border:0;color:#525252;width:32px;height:32px;font-size:var(--cb-fs-label);">\u2191</button>' +
            '<button type="button" data-cb="down" aria-label="Move later" style="appearance:none;font:inherit;cursor:pointer;background:transparent;border:0;color:#525252;width:32px;height:32px;font-size:var(--cb-fs-label);">\u2193</button></span>' +
            '<button type="button" class="cb-ghost" data-cb="remove" aria-label="Remove" style="appearance:none;font:inherit;cursor:pointer;background:transparent;border:0;color:#737373;font-size:var(--cb-fs-helper);line-height:1;padding:6px;">\u2715</button></div>';
        });
        o += '</div>';
      } else {
        o += '<p style="margin:0;font-size:var(--cb-fs-helper);line-height:1.6;color:#A3A3A3;text-wrap:pretty;">Nothing here yet. Add a charm and it’ll show up here, and you can shuffle the order until it hangs the way you want.</p>';
      }
      html += '<div class="cb-order" style="display:flex;flex-direction:column;gap:12px;padding-top:12px;">' + o + '</div>';
      }
      if (state.mobile) barHtml = mobileHeader(selected);
      html += '</section>';

      var total = (selected ? selected.price : 0) + state.build.reduce(function (s, b) { return s + findCharm(b.charmId).price; }, 0);
      html += '<section class="cb-step" style="' + S_STEP + '">' + head("03", "YOUR BUILD", money(total));
      if (selected) {
        html += '<div style="display:flex;flex-direction:column;">' +
          '<div style="' + S_LINE + '"><span style="font-size:var(--cb-fs-body);">' + esc(selected.title) + '</span>' +
          '<span style="' + S_HELPPRICE + '">' + esc(selected.priceLabel) + '</span></div>';
        state.build.forEach(function (b) {
          var c = findCharm(b.charmId);
          html += '<div style="' + S_LINE + '"><span style="font-size:var(--cb-fs-body);color:#525252;">' +
            esc(b.value ? c.title + " \u2014 " + b.value : c.title) + '</span>' +
            '<span style="' + S_HELPPRICE + '">' + esc(c.priceLabel) + '</span></div>';
        });
        html += '<div style="display:flex;align-items:baseline;justify-content:space-between;gap:16px;padding:18px 0 0;">' +
          '<span style="' + S_LBL + 'font-weight:500;">Total</span>' +
          '<span style="font-family:var(--cb-font-heading);font-size:var(--cb-fs-total);font-variant-numeric:tabular-nums;">' + esc(money(total)) + '</span></div></div>';
      }
      html += '<p style="' + S_BODY + '">Every charm is priced the same whether you take one or more. Leave it empty and it wears beautifully on its own.</p>';

      if (state.cart === "done") {
        html += '<div style="border:1px solid #171717;border-radius:3px;padding:20px;display:flex;flex-direction:column;gap:14px;">' +
          '<span style="' + S_LBL + 'font-weight:500;">In your cart — build #' + esc(state.buildId) + '</span>' +
          '<p style="' + S_HELPP + '">' + (state.build.length
            ? (state.build.length === 1 ? 'Your base and charm are' : 'Your base and all ' + state.build.length + ' charms are') + ' listed separately, tagged #' + esc(state.buildId) +
              ' and numbered left to right so we assemble it exactly as you arranged it.'
            : 'Your base is in the cart on its own.') + '</p>' +
          '<div style="' + S_WRAPROW + '">' +
          '<a href="/cart" style="appearance:none;font:inherit;font-size:var(--cb-fs-button);letter-spacing:0.18em;text-transform:uppercase;text-decoration:none;padding:18px 22px;border-radius:2px;border:1px solid #171717;background:#171717;color:#FFFFFF;">View cart</a>' +
          '<button type="button" data-cb="start-another" style="appearance:none;font:inherit;font-size:var(--cb-fs-button);letter-spacing:0.18em;text-transform:uppercase;padding:18px 22px;border-radius:2px;border:1px solid rgba(23,23,23,0.18);background:transparent;color:#525252;cursor:pointer;">Start another</button>' +
          '</div></div>';
      } else {
        if (state.cart === "error" && state.cartMsg) {
          html += '<p class="cb-notice" style="margin:0;padding:14px 16px;border:1px solid #171717;border-radius:3px;font-size:var(--cb-fs-helper);line-height:1.5;">' +
            esc(state.cartMsg) + '</p>';
        }
        var busy = state.cart === "busy";
        var canAdd = !!selected && !busy;
        html += '<button type="button" data-cb="cart"' + (canAdd ? '' : ' disabled') +
          ' style="appearance:none;font:inherit;font-size:var(--cb-fs-button);letter-spacing:0.18em;text-transform:uppercase;' +
          'padding:18px 22px;border-radius:2px;border:1px solid #171717;background:#171717;color:#FFFFFF;' +
          (canAdd ? "cursor:pointer;" : "opacity:0.35;cursor:not-allowed;") + '">' +
          (busy ? "Checking stock\u2026" : "Add to cart") + '</button>';
      }
      html += '</section>';
    }

    stepsEl.innerHTML = html;
    var oldRows = barRoot.querySelector(".cb-rows"), keepTop = oldRows ? oldRows.scrollTop : 0;
    barRoot.innerHTML = barHtml;
    var newRows = barRoot.querySelector(".cb-rows");
    if (newRows) newRows.scrollTop = keepTop;
    positionSticky();
    renderModal();
    renderPortal(selected);
    applyGalleryState();
  }

  function renderModal() {
    var editItem = state.editUid ? state.build.filter(function (b) { return b.uid === state.editUid; })[0] || null : null;
    var charm = editItem ? findCharm(editItem.charmId) : (!state.wide && state.modalId) ? findCharm(state.modalId) : null;
    if (!charm) { modalRoot.innerHTML = ""; return; }
    modalRoot.innerHTML = '<div class="cb-modal-scrim" data-cb="scrim" style="position:fixed;inset:0;z-index:40;background:rgba(23,23,23,0.38);display:flex;align-items:center;justify-content:center;padding:20px;">' +
      '<div class="cb-modal" style="width:100%;max-width:420px;max-height:90vh;overflow-y:auto;background:#FFFFFF;border:1px solid rgba(23,23,23,0.16);border-radius:3px;padding:22px;display:flex;flex-direction:column;gap:18px;">' +
      '<div style="' + imgBg(charm.image, "width:100%;aspect-ratio:3/2;border-radius:2px;background-color:#F5F5F5;") + '"></div>' +
      '<div style="display:flex;flex-direction:column;gap:6px;">' +
      '<div style="display:flex;align-items:baseline;justify-content:space-between;gap:14px;">' +
      '<span style="' + S_PANELTITLE + '">' + esc(charm.title) + '</span>' +
      '<span style="' + S_HELPPRICE + '">' + esc(charm.priceLabel) + '</span></div>' +
      '<p style="' + S_HELPP + '">' + esc(charm.blurb) + ' ' + esc(helperCopy(editItem)) + '</p></div>' +
      '<div style="display:flex;flex-direction:column;gap:10px;">' +
      '<span style="' + S_OPTNAME + '">' + esc(charm.options ? charm.options.name : "") + '</span>' +
      '<div style="' + S_WRAPROW + '">' + optionsHtml(charm, editItem, editItem ? "replace-variant" : "add-variant") + '</div></div>' +
      '<div style="display:flex;flex-direction:column;gap:10px;">' +
      '<button type="button" data-cb="modal-close" style="appearance:none;font:inherit;font-size:var(--cb-fs-button);letter-spacing:0.18em;text-transform:uppercase;padding:14px 20px;border-radius:2px;border:1px solid rgba(23,23,23,0.18);background:transparent;color:#525252;cursor:pointer;">Cancel</button></div>' +
      '</div></div>';
  }

  // WHERE EACH CHARM SITS. One function per mode, each returning {x, y, rot} in
  // percent/degrees for instance i of n. This is the ONLY thing that differs
  // between builder types — the ordered build array, chips row, drag, arrows and
  // left-to-right order are identical everywhere, so index i always means the
  // same charm in the preview and in the chips.
  function placeCurve(a, i, n) {
    var x0 = a.start[0], y0 = a.start[1], x2 = a.end[0], y2 = a.end[1];
    var cx = (x0 + x2) / 2, cy = (y0 + y2) / 2 + a.sag * 2;
    // Equal arc length, not equal t: sample the curve densely, build a cumulative
    // length table, then invert it for fraction (i+1)/(n+1).
    var N = 200, lens = [0], px = x0, py = y0;
    for (var k = 1; k <= N; k++) {
      var tk = k / N, uk = 1 - tk;
      var qx = uk * uk * x0 + 2 * uk * tk * cx + tk * tk * x2, qy = uk * uk * y0 + 2 * uk * tk * cy + tk * tk * y2;
      lens.push(lens[k - 1] + Math.sqrt((qx - px) * (qx - px) + (qy - py) * (qy - py)));
      px = qx; py = qy;
    }
    var target = lens[N] * (i + 1) / (n + 1), lo = 0, hi = N;
    while (hi - lo > 1) { var mid = (lo + hi) >> 1; if (lens[mid] < target) lo = mid; else hi = mid; }
    var seg = lens[hi] - lens[lo];
    var t = (lo + (seg > 0 ? (target - lens[lo]) / seg : 0)) / N, u = 1 - t;
    // Tilt each charm with the chain instead of standing them all upright: the
    // tangent of the curve at t, scaled by charmRotate and clamped so a steep
    // shoulder never lays a charm on its side. Rotation is about the charm's
    // own centre, so it never moves off the curve.
    var dx = 2 * u * (cx - x0) + 2 * t * (x2 - cx);
    var dy = 2 * u * (cy - y0) + 2 * t * (y2 - cy);
    var rot = Math.atan2(dy, dx) * (180 / Math.PI) * (a.charmRotate != null ? a.charmRotate : 0.85);
    rot = Math.max(-55, Math.min(55, rot));
    // Optional authored rotation on start/end interpolates across the arc.
    return { x: u * u * x0 + 2 * u * t * cx + t * t * x2,
      y: u * u * y0 + 2 * u * t * cy + t * t * y2,
      rot: rot + (ptRot(a.start) * u + ptRot(a.end) * t) };
  }
  // Symmetric about the centre: the row grows outward from the middle, so
  // adding a third charm shifts the existing two rather than appending.
  function placeCluster(a, i, n) {
    var offset = (i - (n - 1) / 2) * a.gap;
    return { x: a.center[0] + offset, y: a.center[1], rot: ptRot(a.center) };
  }
  // Hanging from a single point: n charms spread evenly across the arc, centred
  // on vertical, so the angles run -spread/2 … +spread/2 and a lone charm hangs
  // straight down at 0. Each charm is rotated by its own angle so it sits along
  // its line from the pivot rather than standing upright — that tilt is what
  // reads as hanging instead of floating. Adding a third shifts the existing
  // two, same as cluster.
  function placeFan(a, i, n) {
    // Magnitude drives left-to-right order (index 0 always leftmost); the sign of
    // spread only picks whether the fan opens downward (+) or upward (-).
    var mag = Math.abs(a.spread);
    var dir = a.spread < 0 ? -1 : 1;
    var deg = n <= 1 ? 0 : -mag / 2 + (mag * i) / (n - 1);
    var rad = deg * (Math.PI / 180);
    // CSS rotation runs opposite the angle measured from straight-down (screen y
    // points down), so negating it aligns the charm with its own radius; an
    // upward fan mirrors that vertically, which flips the rotation sign too.
    return { x: a.pivot[0] + a.radius * Math.sin(rad),
      y: a.pivot[1] + dir * a.radius * Math.cos(rad),
      rot: -dir * deg + ptRot(a.pivot) };
  }
  // Fixed coordinates, filled left to right. Never redistributes: two charms on
  // a five-slot pin stay in slots 1 and 2.
  function placeSlot(a, i) {
    var p = a.slots[i] || a.slots[a.slots.length - 1];
    return { x: p[0], y: p[1], rot: ptRot(p) };
  }
  function placeCharm(a, i, n) {
    var p = a.mode === "slots" ? placeSlot(a, i)
      : a.mode === "cluster" ? placeCluster(a, i, n)
      : a.mode === "fan" ? placeFan(a, i, n)
      : placeCurve(a, i, n);
    p.rot += a.charmTilt || 0;
    return p;
  }

  // The preview itself: base product photo, then one charm per instance placed
  // by the mode's coordinate function.
  function previewInner(selected, boxStyle) {
    var a = selected.anchor;
    var n = state.build.length;
    var inner = '<div class="cb-preview" style="' + boxStyle + '">' +
      '<div style="' + imgBg(selected.baseImage, "position:absolute;inset:0;background-color:#F5F5F5;") + '"></div>';
    state.build.forEach(function (b, i) {
      var c = findCharm(b.charmId);
      var spot = placeCharm(a, i, n);
      var px = spot.x, py = spot.y, rot = spot.rot;
      var base = "position:absolute;left:" + px.toFixed(2) + "%;top:" + py.toFixed(2) + "%;width:" +
        (a.charmScale * (state.wide ? 100 : 128)).toFixed(2) + "%;aspect-ratio:1/1;" +
        "--cb-rot:" + rot.toFixed(2) + "deg;transform:translate(-50%,-50%) rotate(" + rot.toFixed(2) + "deg);" +
        "animation:cb-drop 240ms ease-out backwards;animation-delay:" + (i * 45) + "ms;";
      var art = charmArt(c, b);
      inner += art
        ? '<img src="' + esc(art) + '" alt="" style="' + base + 'object-fit:contain;">'
        : '<div style="' + base + 'border-radius:50%;background:#E5E5E5;box-shadow:0 1px 3px rgba(23,23,23,0.18);"></div>';
    });
    return inner + '</div>';
  }
  // All breakpoints: the preview sits in the theme gallery. On phones the
  // sticky mobileHeader() repeats it once the gallery scrolls out of view.
  function renderPortal(selected) {
    if (!selected) { portal.style.display = "none"; portal.innerHTML = ""; return; }
    portal.style.cssText = state.mounted
      ? "position:absolute;inset:0;z-index:2147483000;pointer-events:none;"
      : "position:relative;width:100%;margin:0 0 28px;border:1px solid #E5E5E5;border-radius:3px;overflow:hidden;";
    portal.innerHTML = previewInner(selected, state.mounted ? "position:absolute;inset:0;" : "position:relative;width:100%;aspect-ratio:" + state.aspect + ";");
  }

  // PHONE STICKY HEADER. Preview left (60%), reorder list right (40%). The
  // window shows the middle 80% of the base image (10% off top and bottom),
  // full column width unless that would exceed 35vh. The header's height IS
  // that window — the list column is absolutely positioned so it never adds
  // height. The full-size box sits inside offset by -12.5% (10% of the box =
  // 12.5% of the 80% window), so charm coordinates stay percentages of the
  // uncropped image.
  function mobileHeader(selected) {
    var A = state.aspect;
    var rows = state.build.map(function (b, i) {
      var c = findCharm(b.charmId);
      var dragging = state.dragIndex === i;
      var over = state.overIndex === i && state.dragIndex !== null && state.dragIndex !== i;
      return '<div class="cb-row" data-cb-row data-cb-chip="' + i + '" data-cb="chip-edit" style="display:flex;align-items:center;gap:5px;min-height:34px;flex:none;padding-left:7px;border-radius:3px;' +
        'font-size:var(--cb-fs-nano);line-height:1.2;user-select:none;-webkit-user-select:none;-webkit-touch-callout:none;touch-action:' + (state.dragIndex != null ? 'none' : 'pan-y') + ';' +
        'transition:background 120ms ease, color 120ms ease, box-shadow 120ms ease, transform 120ms ease;' +
        (dragging ? 'background:#171717;color:#FFFFFF;border:1px solid #171717;transform:scale(1.03);box-shadow:0 6px 16px rgba(23,23,23,0.25);position:relative;z-index:1;'
          : 'background:' + (over ? '#F5F5F5' : '#FFFFFF') + ';color:#171717;border:1px solid ' + (over || state.editUid === b.uid ? '#171717' : 'rgba(23,23,23,0.14)') + ';') + '">' +
        grip(true, true) +
        '<span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">' + esc(b.value ? c.title + " \u2014 " + b.value : c.title) + '</span>' +
        '<button type="button" class="cb-ghost" data-cb="remove" aria-label="Remove" style="appearance:none;font:inherit;cursor:pointer;background:transparent;border:0;color:inherit;width:40px;height:34px;margin:-1px 0;flex:none;font-size:var(--cb-fs-label);line-height:1;">\u2715</button></div>';
    }).join("");
    var solo = capacityOf(selected) === 1;
    return '<div class="cb-sticky" style="position:fixed;left:0;right:0;top:0;z-index:2147483100;display:flex;gap:8px;' +
      'background:#FFFFFF;border-bottom:1px solid rgba(23,23,23,0.14);box-shadow:0 6px 20px rgba(23,23,23,0.08);font-family:var(--cb-font-body);color:#171717;' +
      'transform:translateY(-110%);visibility:hidden;transition:transform 180ms ease, visibility 0s linear 180ms;">' +
      '<div style="' + (solo ? 'flex:1;' : 'flex:0 0 60%;') + 'background:#F5F5F5;">' +
      '<div style="position:relative;overflow:hidden;margin:0 auto;width:min(100%, calc(35vh * ' + A + ' / 0.8));aspect-ratio:' + A + ' / 0.8;">' +
      previewInner(selected, "position:absolute;left:0;top:-12.5%;width:100%;aspect-ratio:" + A + ";") + '</div></div>' +
      (solo ? '' : '<div style="flex:1;min-width:0;position:relative;">' +
      '<div style="position:absolute;inset:0;display:flex;flex-direction:column;gap:4px;padding:6px 6px 6px 0;">' +
      '<span style="' + S_LBL + 'font-size:var(--cb-fs-nano);letter-spacing:0.1em;font-weight:500;color:#171717;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">Drag to reorder</span>' +
      (rows ? '<div class="cb-rows" style="flex:1;min-height:0;overflow-y:auto;overscroll-behavior:contain;display:flex;flex-direction:column;gap:3px;">' + rows + '</div>'
        : '<p style="margin:0;font-size:var(--cb-fs-nano);line-height:1.4;color:#A3A3A3;">Add a charm and it’ll show up here.</p>') +
      '</div></div>') + '</div>';
  }
  // The header is position:fixed on <body> (a theme ancestor with overflow or
  // transform would break real sticky). It shows once a chain is selected and
  // the theme's product media has scrolled fully above the viewport (fallback
  // without a media node: the builder's own top), hides during a modal, and
  // slides away once the builder itself has scrolled past.
  function positionSticky() {
    var bar = barRoot.querySelector(".cb-sticky");
    if (!bar) return;
    if (!MQ_MOBILE.matches) { if (state.mobile) setState({ mobile: false }); return; }
    var media = mountTarget && mountTarget.isConnected ? mountTarget : findTarget();
    var r = root.getBoundingClientRect(), h = bar.offsetHeight;
    // Bound to the builder: end at whichever comes first of the add-to-cart
    // button, the builder's steps, or the builder's own Shopify section.
    var sec = root.closest(".shopify-section") || root;
    var atc = stepsEl.querySelector('[data-cb="cart"]');
    var end = Math.min(sec.getBoundingClientRect().bottom, stepsEl.getBoundingClientRect().bottom,
      atc ? atc.getBoundingClientRect().bottom : Infinity);
    var past = media ? media.getBoundingClientRect().bottom <= 0 : r.top <= 0;
    var show = past && end > h && r.top < window.innerHeight && !state.modalId && !state.editUid;
    bar.style.transform = show ? "translateY(0)" : "translateY(-110%)";
    bar.style.visibility = show ? "visible" : "hidden";
    bar.style.transition = show ? "transform 180ms ease" : "transform 180ms ease, visibility 0s linear 180ms";
  }
  window.addEventListener("scroll", positionSticky, { passive: true });
  window.addEventListener("resize", positionSticky);


/* ==========================================================================
   EVENTS
   Two delegated listeners for the entire UI — innerHTML replacement would orphan per-element handlers.
   ========================================================================== */

  document.addEventListener("pointerdown", function (e) {
    var row = e.target.closest ? e.target.closest("[data-cb-row]") : null;
    if (row && barRoot.contains(row) && !e.target.closest('[data-cb="remove"]')) {
      var ri = Number(row.getAttribute("data-cb-chip"));
      if (e.pointerType === "mouse") onGrab(ri, e, null); else startPress(ri, e);
      return;
    }
    var grip = e.target.closest ? e.target.closest('[data-cb="grab"]') : null;
    if (!grip || (!root.contains(grip) && !barRoot.contains(grip))) return;
    var chip = grip.closest("[data-cb-chip]");
    if (chip) onGrab(Number(chip.getAttribute("data-cb-chip")), e, grip);
  });

  document.addEventListener("click", function (e) {
    var el = e.target.closest ? e.target.closest("[data-cb]") : null;
    if (!el || (!root.contains(el) && !modalRoot.contains(el) && !barRoot.contains(el))) return;
    if (Date.now() < suppressClickUntil) return;             // release after a drag isn't a tap
    var action = el.getAttribute("data-cb");
    var chip = el.closest("[data-cb-chip]");
    var i = chip ? Number(chip.getAttribute("data-cb-chip")) : null;
    var blocked = el.getAttribute("data-cb-blocked") === "1";

    if (action === "chain") {
      if (!blocked) { var ch = findChain(el.getAttribute("data-cb-id")); if (ch) switchChain(ch); }
    } else if (action === "toggle-chain") {
      setState({ chainOpen: !state.chainOpen });
    } else if (action === "cat") {
      setState({ category: el.getAttribute("data-cb-val") || null, modalId: null });
    } else if (action === "metal") {
      setState({ metal: el.getAttribute("data-cb-val") || null, modalId: null });
    } else if (action === "charm") {
      if (blocked) return;
      var c = findCharm(el.getAttribute("data-cb-id"));
      if (!c) return;
      if (!c.options) addSimple(c); else setState({ modalId: c.id, editUid: null });
    } else if (action === "qty-dec") {
      e.stopPropagation();
      removeLast(el.getAttribute("data-cb-id"));
    } else if (action === "qty-inc") {
      e.stopPropagation();
      if (blocked) return;
      var qc = findCharm(el.getAttribute("data-cb-id"));
      if (!qc) return;
      if (!qc.options) addSimple(qc); else setState({ modalId: qc.id, editUid: null });
    } else if (action === "add-variant" || action === "replace-variant") {
      if (blocked) return;
      var vc = findCharm(el.getAttribute("data-cb-charm"));
      var val = el.getAttribute("data-cb-val");
      var opt = vc && vc.options ? vc.options.values.filter(function (o) { return o.value === val; })[0] : null;
      if (!opt) return;
      if (action === "add-variant") addVariant(vc, opt);
      else if (state.editUid) replaceVariant(state.editUid, opt);
    } else if (action === "chip-edit") {
      var b = state.build[i];
      var bc = b ? findCharm(b.charmId) : null;
      if (bc && bc.options) setState({ editUid: b.uid, modalId: null });
    } else if (action === "up") { e.stopPropagation(); move(i, i - 1); }
    else if (action === "down") { e.stopPropagation(); move(i, i + 1); }
    else if (action === "remove") { e.stopPropagation(); removeUid(state.build[i].uid); }
    else if (action === "panel-close" || action === "modal-close") setState({ modalId: null, editUid: null });
    else if (action === "scrim") { if (e.target === el) setState({ modalId: null, editUid: null }); }
    else if (action === "cart") { if (!el.disabled) addToCart(); }
    else if (action === "start-another") startAnother();
  });

  window.matchMedia("(min-width: 1000px)").addEventListener("change", function (e) { setState({ wide: e.matches }); });
  function syncMobile() { if (state.mobile !== MQ_MOBILE.matches) setState({ mobile: MQ_MOBILE.matches }); }
  if (MQ_MOBILE.addEventListener) MQ_MOBILE.addEventListener("change", syncMobile); else MQ_MOBILE.addListener(syncMobile);
  window.addEventListener("resize", syncMobile);
  window.addEventListener("load", syncMobile);


/* ==========================================================================
   BOOT
   Render immediately (chain step is usable at once), then load charms.
   ========================================================================== */

  render();
  loadCharms();
  attemptMount();
  startRetry();
  document.addEventListener("DOMContentLoaded", attemptMount);
  window.addEventListener("load", attemptMount);
  // Re-validate the mount, not just its presence: the theme renders different
  // gallery nodes per breakpoint, so a portal parked in the old layout's slide
  // stays "connected" while being invisible. Remount whenever a better (or the
  // only visible) target appears.
  function revalidateMount() {
    if (!portal.isConnected || !state.mounted) { startRetry(); return; }
    var best = findTarget();
    if (!best || !isVisible(mountTarget)) { state.mounted = false; galleryKey = null; startRetry(); return; }
    if (best !== mountTarget) {
      if (galleryWrap) galleryWrap.classList.remove("cb-media-hidden");
      galleryWrap = null; galleryKey = null; mountPreview();
    } else applyGalleryState();
  }
  setInterval(revalidateMount, 2000);
  var resizeTimer = null;
  window.addEventListener("resize", function () {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(revalidateMount, 250);
  });
})();
